import type { Messages } from "../i18n/types";
import type { AppState } from "./renderer";
import { assessEvidence, connectionTelemetry } from "../shared/evidence";
import { areTimezonesEquivalent } from "./timezone";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function createTable(sourceHeader: string, valueHeader: string) {
  const table = el("div", "fraud-provider-table");
  const thead = el("div", "ip-provider-row fraud-provider-header");
  thead.append(
    el("span", "fraud-provider-col col-source", sourceHeader),
    el("span", "fraud-provider-col col-value", valueHeader)
  );
  table.append(thead);

  const addRow = (label: string, value: string, mono = true) => {
    const row = el("div", "ip-provider-row");
    row.append(
      el("span", "fraud-provider-col col-source", label),
      el("span", `fraud-provider-col col-value ${mono ? "mono" : ""}`, value)
    );
    table.append(row);
    return row;
  };

  return { table, addRow };
}

export function buildConnectionCard(state: AppState, copy: Messages): HTMLElement {
  const c = copy.evidence;
  const n = state.scan?.network;
  const unknown = copy.common.unavailable;
  const telemetry = n ? connectionTelemetry(n) : {};

  const card = el("section", "fraud-score-card ping-card");

  // Header matching console cards
  const header = el("div", "fraud-score-header");
  const titleWrap = el("div", "fraud-score-title-wrap");
  titleWrap.append(
    el("h3", "fraud-score-title", c.connection),
    el("span", "fraud-score-eyebrow", c.connectionSub)
  );
  header.append(titleWrap);

  const scoreDisplay = el("div", "fraud-score-display");
  const rttNum = el(
    "div",
    "ip-display-number connection-rtt-number",
    telemetry.rtt !== undefined ? String(telemetry.rtt) : "—"
  );
  const rttMax = el("span", "fraud-score-max", " ms");
  scoreDisplay.append(rttNum, rttMax);
  header.append(scoreDisplay);
  card.append(header);

  // Table
  const { table, addRow } = createTable(copy.cards.fraudCard.colSource, copy.cards.fraudCard.colValue);
  addRow(c.connection, telemetry.rtt === undefined ? unknown : `${telemetry.rtt} ms`);
  addRow(c.protocol, n?.httpProtocol ?? unknown);
  addRow(c.transport, telemetry.transport ?? unknown);
  addRow(c.edge, n?.colo ?? unknown);
  addRow("TLS", n?.tlsVersion ?? unknown);
  addRow(c.delivery, n?.deliveryRate === undefined ? unknown : `${n.deliveryRate} ${c.deliveryUnit}`);
  card.append(table);

  // Explanation method wrap with accessible dialog trigger
  const method = el("div", "ping-method-wrap");
  const methodNote = el("p", "ping-method-note", c.rttNote);
  const button = el("button", "ping-explain-button", "!");
  button.type = "button";
  button.setAttribute("aria-label", c.rttHelp);
  button.setAttribute("aria-haspopup", "dialog");
  button.setAttribute("aria-controls", "info-dialog");
  button.addEventListener("click", () => {
    const dialog = document.querySelector<HTMLDialogElement>("#info-dialog");
    const body = dialog?.querySelector<HTMLElement>("[data-dialog-copy]");
    if (!dialog || !body) return;
    body.textContent = c.rttExplanation;
    dialog.addEventListener("close", () => button.focus(), { once: true });
    dialog.showModal();
  });
  method.append(methodNote, button);
  card.append(method);

  // Contextual notes
  card.append(
    el("p", "fraud-score-note", c.edgeNote),
    el("p", "fraud-score-note", c.deliveryNote)
  );

  return card;
}

export function buildEvidenceSections(state: AppState, copy: Messages): HTMLElement {
  const c = copy.evidence;
  const unknown = copy.common.unavailable;
  const result = el("div", "evidence-sections");
  const scan = state.scan;
  if (!scan) return result;

  // 1. Network Registration Card (RDAP / RIR)
  const registrationCard = el("section", "fraud-score-card evidence-card evidence-card-registration");
  const regHeader = el("div", "fraud-score-header");
  const regTitleWrap = el("div", "fraud-score-title-wrap");
  regTitleWrap.append(
    el("h3", "fraud-score-title", c.registration),
    el("span", "fraud-score-eyebrow", `${c.rir} · RDAP`)
  );
  regHeader.append(regTitleWrap);
  registrationCard.append(regHeader);

  const r = scan.registration;
  const regTable = createTable(copy.cards.fraudCard.colSource, copy.cards.fraudCard.colValue);
  regTable.addRow(c.rir, r?.rir ?? unknown);
  regTable.addRow(c.network, r?.network ?? unknown);
  regTable.addRow(c.organization, r?.organization ?? unknown);
  regTable.addRow(c.country, r?.countryCode ?? unknown);
  regTable.addRow(c.range, r ? r.cidrs?.join(", ") || `${r.startAddress} – ${r.endAddress}` : unknown);
  registrationCard.append(regTable.table, el("p", "fraud-score-note", c.registrationOnly));

  // 2. Evidence Sources Card (Cross-Source Intelligence)
  const assessment = assessEvidence(scan);
  const sourcesCard = el("section", "fraud-score-card evidence-card evidence-card-sources");

  const sourcesHeader = el("div", "fraud-score-header");
  const sourcesTitleWrap = el("div", "fraud-score-title-wrap");
  sourcesTitleWrap.append(
    el("h3", "fraud-score-title", c.sources),
    el("span", "fraud-score-eyebrow", "MULTI-SOURCE INTELLIGENCE")
  );
  sourcesHeader.append(sourcesTitleWrap);

  const badgesWrap = el("div", "evidence-badges-wrap");
  let confidenceClass = "evidence-band-neutral";
  if (assessment.confidence === "high") confidenceClass = "fraud-band-low";
  else if (assessment.confidence === "medium") confidenceClass = "fraud-band-medium";
  else if (assessment.confidence === "low") confidenceClass = "fraud-band-high";

  let originClass = "";
  if (assessment.origin === "some_disagreement") originClass = "origin-disagreement";
  else if (assessment.origin === "insufficient") originClass = "origin-insufficient";

  const confBadge = el(
    "div",
    `fraud-score-band ${confidenceClass}`,
    `${c.confidence}: ${c.levels[assessment.confidence]}`
  );
  const origBadge = el(
    "div",
    `evidence-origin-badge ${originClass}`,
    `${c.origin}: ${c.origins[assessment.origin]}`
  );
  badgesWrap.append(confBadge, origBadge);
  sourcesHeader.append(badgesWrap);
  sourcesCard.append(sourcesHeader);

  const srcTable = createTable(copy.cards.fraudCard.colSource, copy.cards.fraudCard.colValue);
  srcTable.addRow(
    "Cloudflare",
    [scan.network.countryCode, scan.network.asn ? `AS${scan.network.asn}` : "", scan.network.organization]
      .filter(Boolean)
      .join(" · ") || unknown
  );
  if (r) srcTable.addRow(r.rir + " / RDAP", [r.countryCode, r.organization, c.registrationOnly].filter(Boolean).join(" · "));
  if (scan.reputation) srcTable.addRow(scan.reputation.provider === "tor_project" ? "Tor Project" : "Scamalytics", c.reputationOnly);
  if (scan.reputation?.provider !== "tor_project" && scan.reputation?.torSource === "tor_project") srcTable.addRow("Tor Project", copy.common.observed);
  if (scan.ipinfo) srcTable.addRow("IPinfo", [scan.ipinfo.countryCode, scan.ipinfo.asnNumber, scan.ipinfo.asnName, c.identityOnly].filter(Boolean).join(" · "));
  if (scan.ip2location) srcTable.addRow("IP2Location", [scan.ip2location.countryCode, scan.ip2location.asnName, c.identityOnly].filter(Boolean).join(" · "));
  if (scan.abuseipdb) srcTable.addRow("AbuseIPDB", c.abuseNote);
  if (state.browser || state.webrtc) srcTable.addRow("Browser / WebRTC", c.browserOnly);
  sourcesCard.append(srcTable.table);

  if (assessment.disagreements.length) {
    const alert = el("div", "evidence-disagreement-alert");
    alert.append(el("span", "alert-icon", "⚠"), el("span", "", c.disagreement));
    sourcesCard.append(alert);
  }

  sourcesCard.append(el("h4", "evidence-sub-heading", c.why));
  const reasonsList = el("div", "evidence-reasons-list");
  for (const reason of assessment.reasons) {
    const item = el("div", "evidence-reason-item reason-agreement");
    item.append(el("span", "reason-icon", "✓"), el("span", "reason-text", c.reasons[reason]));
    reasonsList.append(item);
  }
  for (const reason of assessment.disagreements) {
    const item = el("div", "evidence-reason-item reason-disagreement");
    item.append(el("span", "reason-icon", "⚠"), el("span", "reason-text", c.reasons[reason]));
    reasonsList.append(item);
  }
  sourcesCard.append(reasonsList);

  sourcesCard.append(
    el("p", "fraud-score-note", c.originNote),
    el("p", "fraud-score-note", c.methodRules)
  );

  // 3. Environment Consistency Card
  const consistencyCard = el("section", "fraud-score-card evidence-card evidence-card-consistency");
  const conHeader = el("div", "fraud-score-header");
  const conTitleWrap = el("div", "fraud-score-title-wrap");
  conTitleWrap.append(
    el("h3", "fraud-score-title", c.consistency),
    el("span", "fraud-score-eyebrow", "CROSS-ENVIRONMENT VERIFICATION")
  );
  conHeader.append(conTitleWrap);
  consistencyCard.append(conHeader);

  const conTable = createTable(copy.cards.fraudCard.colSource, copy.cards.fraudCard.colValue);
  conTable.addRow(c.dualstack, c.dualstackNote);
  conTable.addRow(
    c.timezone,
    scan.network.timezone && state.browser?.timezone
      ? areTimezonesEquivalent(scan.network.timezone, state.browser.timezone)
        ? c.timezoneSame
        : c.timezoneDifferent
      : unknown
  );
  let region: string | undefined;
  try {
    region = state.browser?.language ? new Intl.Locale(state.browser.language).region : undefined;
  } catch {
    /* Untrusted browser setting. */
  }
  conTable.addRow(
    c.locale,
    !region
      ? c.localeUnknown
      : !scan.network.countryCode
        ? unknown
        : region === scan.network.countryCode
          ? c.localeSame
          : c.localeDifferent
  );
  conTable.addRow(
    c.webrtc,
    !state.webrtc?.supported
      ? unknown
      : state.webrtc.additionalPublicIps.length
        ? `${c.webrtcExtra}: ${state.webrtc.additionalPublicIps.join(", ")}`
        : state.webrtc.completed
          ? c.webrtcNone
          : c.webrtcIncomplete
  );
  conTable.addRow(c.candidateTypes, state.webrtc?.candidateTypes?.join(", ") || unknown);
  consistencyCard.append(conTable.table, el("p", "fraud-score-note", c.webrtcNote));

  // Assemble container in natural mobile flow
  result.append(registrationCard, sourcesCard, consistencyCard);

  const disclaimerWrap = el("div", "evidence-disclaimer-wrap");
  disclaimerWrap.append(el("p", "privacy-note", c.disclaimer));
  result.append(disclaimerWrap);

  return result;
}
